import { useEffect, useState } from 'react';
import { ArrowRight, ChevronRight, Minus, Plus, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { executeRecaptcha, hasConsent, openCookieSettings } from '../tools/cookieConsent';
import FormProtectionGate from './FormProtectionGate';
import toast from 'react-hot-toast';
import { privacyPolicyDate, privacyPolicySections, privacyPolicyTitle } from '../data/privacyPolicy';
import { contactForms as tabs } from '../data/contactForms';

const DEBUG_MODE = false;

const states = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL',
  'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const industries = [
  'Paper Mills',
  'Corrugated Container Manufacturers',
  'Nonwoven Mills',
  'Textile Mills & Cotton Gins',
  'Distribution Centers',
  'Manufacturers & Industrial Plants',
  'Warehouses',
  'Recycling Centers',
  'Other',
];

const materialTypes = ['Paper Trim', 'Waste Paper', 'Cardboard', 'Boxboard', 'Non-Wovens', 'Plastic Wrap', 'Plastic Bottles', 'Cotton', 'Wool', 'Fiberglass', 'Man-Made Fibers', 'Carpet Remnants', 'Foam Rubber', 'Tobacco', 'Films', 'Non-Ferrous Metals', 'Used Clothing', 'Other'];
const conditionOptions = ['Whole', 'Trim', 'Shredded', 'Chopped', 'Other'];
const preparationOptions = ['Hogger', 'Shredder', 'Slitter', 'None', 'Other'];
const feedingOptions = ['Conveyor', 'Cart Dumper', 'Air System', 'Manually by Hand', 'Other'];
const handlingOptions = ['Forklift', 'Crane', 'Manually', 'Other'];

const wireItems = [
  ['WR0000', 'Auto-Tie Wire', '10 ga', "2053'", '100 # Boxes', '', '36 Pallet'],
  ['WR0010', 'Auto-Tie Wire', '10 ga', "1026.5'", '50 # Boxes', '', '45 Pallet'],
  ['WR0020', 'Auto-Tie Wire', '11 ga', "2582'", '100 # Boxes', '', '36 Pallet'],
  ['WR0030', 'Auto-Tie Wire', '11 ga', "1291'", '50 # Boxes', '', '45 Pallet'],
  ['WR0040', 'Auto-Tie Wire', '12 ga', "3362'", '100 # Boxes', '', '36 Pallet'],
  ['WR0050', 'Auto-Tie Wire', '12 ga', "1681'", '50 # Boxes', '', '45 Pallet'],
  ['WR0100', 'Single Loop Wire', '14 ga', "14'-0", '', '250', ''],
  ['WR0105', 'Single Loop Wire', '14 ga', "13'-0", '', '250', ''],
  ['WR0200', 'Single Loop Wire', '12 ga', "14'-0", '', '125', ''],
  ['WR0205', 'Single Loop Wire', '12 ga', "13'-0", '', '125', ''],
  ['WR0300', 'Single Loop Wire', '11 ga', "21'-0", '', '125', ''],
  ['WR0305', 'Single Loop Wire', '11 ga', "21'-0", '', '62', ''],
  ['WR0400', 'Single Loop Wire', '12 ga', "21'-0", '', '125', ''],
  ['WR01000', 'Quick Lock Wire Extensions', '12 ga', '6"', '', '500', ''],
  ['WR01100', 'Quick Lock Wire', '12 ga', '7\'-8"', '', '250', ''],
  ['WR01105', 'Quick Lock Wire', '12 ga', '7\'-4"', '', '250', ''],
  ['WR01200', 'Quick Lock Wire', '11 ga', "12'-0", '', '125', ''],
  ['WR01205', 'Quick Lock Wire', '11 ga', '13\'-6"', '', '125', ''],
  ['WR01210', 'Quick Lock Wire', '11 ga', "17'-0", '', '100', ''],
  ['WR01215', 'Quick Lock Wire', '11 ga', '10\'-3"', '', '250', ''],
  ['WR01300', 'Quick Lock Wire', '10 ga', '16\'-6"', '', '125', ''],
  ['WR01400', 'Quick Lock Wire', '12.5 ga', '13\'-6"', '', '125', ''],
];

function TextField({ label, name, type = 'text', required = false, size = 'half' }) {
  return (
    <label className={`form-field ${size}`}>
      <span>{label}{required ? '*' : ''}</span>
      <input name={name} type={type} required={required} />
    </label>
  );
}

function SelectField({ label, name, options, required = false, size = 'half' }) {
  return (
    <label className={`form-field ${size}`}>
      <span>{label}{required ? '*' : ''}</span>
      <select name={name} required={required}>
        <option value="">Select</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function TextArea({ label, name, required = false, rows = 5, size = 'wide' }) {
  return (
    <label className={`form-field ${size}`}>
      <span>{label}{required ? '*' : ''}</span>
      <textarea name={name} rows={rows} required={required} />
    </label>
  );
}

function CheckboxGrid({ title, name, options }) {
  return (
    <fieldset className="checkbox-panel">
      <legend>{title}</legend>
      <div>
        {options.map((option) => (
          <label key={option}>
            <input type="checkbox" name={name} value={option} />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function RadioGroup({ title, name, options, value, onChange }) {
  return (
    <fieldset className="checkbox-panel radio-panel">
      <legend>{title}</legend>
      <div>
        {options.map((option) => (
          <label key={option}>
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={(event) => onChange(event.target.value)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ContactDetails({ simple = false }) {
  if (simple) {
    return (
      <>
        <TextField label="First Name" name="firstName" required />
        <TextField label="Last Name" name="lastName" required />
        <TextField label="Email" name="email" type="email" required />
        <TextField label="Phone" name="phone" type="tel" required />
        <SelectField label="Country" name="country" options={['United States', 'Canada', 'Mexico', 'Other']} required size="half" />
      </>
    );
  }

  return (
    <>
      <TextField label="First Name" name="firstName" required />
      <TextField label="Last Name" name="lastName" required />
      <TextField label="Company" name="company" required />
      <TextField label="Job Title" name="companyTitle" />
      <TextField label="Email" name="email" type="email" required />
      <TextField label="Phone" name="phone" type="tel" required />
      <TextField label="Street Address" name="address" required size="wide" />
      <TextField label="Address Line 2" name="addressLine2" />
      <TextField label="City" name="city" required />
      <SelectField label="State" name="state" options={states} size="short" />
      <TextField label="Zip" name="zipCode" size="short" />
      <SelectField label="Industry" name="industry" options={industries} size="wide" />
    </>
  );
}

function QuoteFields() {
  return (
    <>
      <CheckboxGrid title="Material types" name="materialTypes" options={materialTypes} />
      {/* Temporarily hidden on the Request Quote form; retained for future use.
      <CheckboxGrid title="Material condition" name="condition" options={conditionOptions} />
      <TextField label="Physical dimensions of materials" name="physicalDimensions" size="half" />
      <CheckboxGrid title="Feeding method" name="feedingMethod" options={feedingOptions} />
      <TextField label="Estimated volume" name="estimatedVolume" size="half" />
      <TextField label="Hours of operation per week" name="workHours" size="short" />
      */}
    </>
  );
}

function QuestionnaireFields({ futureIncrease, setFutureIncrease }) {
  return (
    <>
      <CheckboxGrid title="Material types" name="materialTypes" options={materialTypes} />
      <CheckboxGrid title="Material condition" name="materialCondition" options={conditionOptions} />
      <TextField label="Physical dimensions of materials" name="physicalDimensions" size="half" />
      <CheckboxGrid title="Material preparation" name="preparation" options={preparationOptions} />
      <CheckboxGrid title="Feeding into baler" name="feeding" options={feedingOptions} />
      <CheckboxGrid title="Finished bale handling" name="handling" options={handlingOptions} />
      <TextField label="Estimated pounds or tons per hour" name="estimatedVolume" size="half" />
      <RadioGroup title="Do you expect a future volume increase?" name="futureIncrease" options={['Yes', 'No']} value={futureIncrease} onChange={setFutureIncrease} />
      {futureIncrease === 'Yes' ? (
        <TextField label="By what percentage?" name="futureIncreasePercentage" required size="half" />
      ) : null}
      <CheckboxGrid title="Shifts per day" name="shifts" options={['1', '2', '3']} />
      <TextField label="Work hours per week" name="workHours" size="short" />
      <CheckboxGrid title="Can material samples be furnished?" name="samples" options={['Yes', 'No']} />
      <CheckboxGrid title="Is grade separation required?" name="gradeSeparation" options={['Yes', 'No']} />
      <TextField label="Desired bale weight" name="baleWeight" size="short" />
      <TextField label="Bale length" name="baleLength" size="short" />
      <TextField label="Bale width" name="baleWidth" size="short" />
      <TextField label="Bale height" name="baleHeight" size="short" />
      <TextField label="Voltage" name="voltage" size="short" />
      <TextField label="Phase" name="phase" size="short" />
      <TextField label="Cycle" name="cycle" size="short" />
      <TextArea label="Other factors" name="otherFactors" rows={4} />
    </>
  );
}

function WireFields({ quantities, setQuantities }) {
  const step = (index, delta) => {
    setQuantities((current) => {
      const value = Number.parseInt(current[index] || '0', 10);
      const next = Math.max(0, value + delta);
      return { ...current, [index]: next ? String(next) : '' };
    });
  };

  return (
    <div className="wire-table-wrap">
      <div className="wire-table">
        <div className="wire-row wire-head">
          <span>Qty</span><span>C+M #</span><span>Type</span><span>Gauge</span><span>Length</span><span>Lbs.</span><span>Pcs/Bundle</span><span>Boxes/Pallet</span>
        </div>
        {wireItems.map((item, index) => (
          <div className="wire-row" key={`${item[0]}-${index}`}>
            <span className="qty-stepper">
              <button type="button" onClick={() => step(index, -1)} aria-label={`Decrease quantity for ${item[0]}`}><Minus size={14} /></button>
              <input value={quantities[index] || ''} onChange={(event) => setQuantities((current) => ({ ...current, [index]: event.target.value.replace(/[^\d]/g, '') }))} aria-label={`Quantity for ${item[0]}`} />
              <button type="button" onClick={() => step(index, 1)} aria-label={`Increase quantity for ${item[0]}`}><Plus size={14} /></button>
            </span>
            {item.map((cell, cellIndex) => <span key={`${item[0]}-${cellIndex}`}>{cell || '-'}</span>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function PolicyText({ text }) {
  const parts = text.split(/(privacy@baling\.com)/g);

  return parts.map((part, index) => (
    part === 'privacy@baling.com'
      ? <a href="mailto:privacy@baling.com" key={`${part}-${index}`}>{part}</a>
      : part
  ));
}

function PolicyBlocks({ blocks }) {
  return (
    <>
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`;

        if (block.type === 'heading') {
          return <h3 key={key}>{block.text}</h3>;
        }

        if (block.type === 'list') {
          return (
            <ul key={key}>
              {block.items.map((item) => <li key={item}><PolicyText text={item} /></li>)}
            </ul>
          );
        }

        if (block.type === 'nested-list') {
          return (
            <ul key={key}>
              {block.items.map((item) => (
                <li key={item.text}>
                  <PolicyText text={item.text} />
                  <ul>
                    {item.items.map((child) => <li key={child}><PolicyText text={child} /></li>)}
                  </ul>
                </li>
              ))}
            </ul>
          );
        }

        return <p key={key}><PolicyText text={block.text} /></p>;
      })}
    </>
  );
}

function PrivacyPolicyModal({ onClose }) {
  const [openSection, setOpenSection] = useState('');

  return (
    <div className="policy-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="policy-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="policy-modal-head">
          <div>
            <h2 id="policy-modal-title">{privacyPolicyTitle}</h2>
            <span>{privacyPolicyDate}</span>
          </div>
          <button type="button" className="policy-modal-close" onClick={onClose} aria-label="Close Privacy Policy">
            <X size={20} />
          </button>
        </div>
        <div className="policy-modal-body">
          {privacyPolicySections.map((section) => {
            const isOpen = openSection === section.id;

            return (
              <div className="policy-accordion" key={section.id}>
                <button
                  type="button"
                  className="policy-accordion-trigger"
                  onClick={() => setOpenSection(isOpen ? '' : section.id)}
                  aria-expanded={isOpen}
                >
                  <span>
                    <ChevronRight size={22} className={isOpen ? 'open' : ''} />
                    {section.title}
                  </span>
                </button>
                {isOpen ? (
                  <div className="policy-accordion-panel">
                    <PolicyBlocks blocks={section.blocks} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="policy-modal-foot">
          <Link to="/privacy-policy" target="_blank" rel="noreferrer">Open Full Privacy Policy</Link>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </section>
    </div>
  );
}

function ContactForm({ initialTab = 'general', heading, eyebrow, formId = 'contact-form' }) {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState({});
  const [futureIncrease, setFutureIncrease] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const active = tabs.find((tab) => tab.key === initialTab) || tabs[0];

  useEffect(() => {
    setFutureIncrease('');
    setPrivacyAccepted(false);
    setSubmitStatus({ type: '', message: '' });
  }, [initialTab]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;
    if (!hasConsent('captcha')) {
      openCookieSettings();
      return;
    }

    const form = event.currentTarget;
    const data = new FormData(form);
    const get = (name) => data.get(name)?.toString().trim() || '';
    const getAll = (name) => data.getAll(name).map((value) => value.toString()).filter(Boolean);
    const clean = (value) => {
      if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not provided';
      return value || 'Not provided';
    };

    if (!privacyAccepted) {
      toast.error('Submitting this communication request requires agreement to the Privacy Policy.');
      setSubmitStatus({ type: 'error', message: 'Please agree to the Privacy Policy before sending your request.' });
      return;
    }

    if (active.key === 'questionnaire' && futureIncrease === 'Yes' && !get('futureIncreasePercentage')) {
      toast.error('Please enter the expected future increase percentage.');
      setSubmitStatus({ type: 'error', message: 'Please enter the expected future increase percentage.' });
      return;
    }

    const payload = {
      form: active.formName || active.title,
      firstName: clean(get('firstName')),
      lastName: clean(get('lastName')),
      company: clean(get('company')),
      jobTitle: clean(get('companyTitle')),
      email: clean(get('email')),
      phone: clean(get('phone')),
      country: clean(get('country')),
      streetAddress: clean(get('address')),
      city: clean(get('city')),
      state: clean(get('state')),
      zip: clean(get('zipCode')),
      industry: clean(get('industry')),
      message: clean(get('comments') || get('otherFactors') || `${active.label} submission`),
      additionalFields: {
        addressLine2: clean(get('addressLine2')),
        materialTypes: clean(getAll('materialTypes')),
        materialCondition: clean(getAll('materialCondition').length ? getAll('materialCondition') : getAll('condition')),
        physicalDimensions: clean(get('physicalDimensions')),
        materialPreparationType: clean(getAll('preparation')),
        feedingMethod: clean(getAll('feeding').length ? getAll('feeding') : getAll('feedingMethod')),
        baleHandling: clean(getAll('handling')),
        estimatedVolume: clean(get('estimatedVolume')),
        futureIncrease: clean(futureIncrease || get('futureIncrease')),
        futureIncreasePercentage: futureIncrease === 'Yes' ? clean(get('futureIncreasePercentage')) : 'Not applicable',
        shiftsPerDay: clean(getAll('shifts')),
        workHours: clean(get('workHours')),
        samples: clean(getAll('samples')),
        gradeSeparation: clean(getAll('gradeSeparation')),
        baleWeight: clean(get('baleWeight')),
        baleLength: clean(get('baleLength')),
        baleWidth: clean(get('baleWidth')),
        baleHeight: clean(get('baleHeight')),
        voltage: clean(get('voltage')),
        phase: clean(get('phase')),
        cycle: clean(get('cycle')),
        wireQuantities: clean(Object.entries(quantities).map(([index, quantity]) => `Item ${Number(index) + 1}: ${quantity}`)),
      },
    };

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      if (!DEBUG_MODE && !import.meta.env.DEV) {
        const token = await executeRecaptcha(`${active.key}_submit`);

        const verifyResponse = await fetch('/php/ReCaptchaCM.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ response: token }),
        });

        const raw = (await verifyResponse.text() || '').trim();

        if (!verifyResponse.ok) {
          throw new Error(`reCAPTCHA server error (${verifyResponse.status}).`);
        }

        if (raw.toLowerCase() !== 'success') {
          throw new Error('reCAPTCHA verification failed.');
        }
      }

      if (!hasConsent('captcha')) throw new Error('Form protection consent was withdrawn.');

      const response = await fetch('/php/ContactCM.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      const result = text ? JSON.parse(text) : {};

      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || `Form submission failed (${response.status}).`);
      }

      toast.success('Your request has been submitted successfully.');
      setFutureIncrease('');
      setPrivacyAccepted(false);
      form.reset();
      navigate(active.confirmationPath);
    } catch (error) {
      toast.error(error?.message || 'There was a problem submitting your request. Please try again.');
      setSubmitStatus({
        type: 'error',
        message: error?.message || 'There was a problem submitting your request. Please try again or call C&M directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormProtectionGate id={formId}>
        <form className="mega-form" onSubmit={handleSubmit}>
          <>
            <span className="eyebrow">{eyebrow || active.eyebrow}</span>
            <h2>{heading || active.title}</h2>
            <div className="form-grid">
              <ContactDetails simple={active.key === 'general'} />
              {active.key === 'general' ? <TextArea label="Comments" name="comments" required /> : null}
              {active.key === 'quote' ? <QuoteFields /> : null}
              {active.key === 'questionnaire' ? <QuestionnaireFields futureIncrease={futureIncrease} setFutureIncrease={setFutureIncrease} /> : null}
              {active.key === 'wire' ? <WireFields quantities={quantities} setQuantities={setQuantities} /> : null}
            </div>
            <label className="terms-check">
              <input
                type="checkbox"
                required
                checked={privacyAccepted}
                onChange={(event) => setPrivacyAccepted(event.target.checked)}
              />
              <span>
                I agree to share these details so C+M Baling Systems can respond to this request, and I agree to the{' '}
                <button type="button" className="policy-link-button" onClick={() => setPolicyOpen(true)}>Privacy Policy</button>.
              </span>
            </label>
            {submitStatus.message ? (
              <p className={`form-status ${submitStatus.type}`}>{submitStatus.message}</p>
            ) : null}
            <button className="button" type="submit" disabled={isSubmitting || !privacyAccepted}>
              {isSubmitting ? 'Sending...' : 'Send request'} <ArrowRight size={16} />
            </button>
          </>
        </form>
      </FormProtectionGate>
      {policyOpen ? <PrivacyPolicyModal onClose={() => setPolicyOpen(false)} /> : null}
    </>
  );
}

export default ContactForm;
